import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowLeft, Check, ChevronRight, Loader2, Lock, Plus, Trash2, X } from "lucide-react";

import { AdminCard, ListState } from "@/components/admin/primitives";
import { ChipPicker } from "@/components/admin/catalogo/ChipPicker";
import { ImageDrop } from "@/components/admin/catalogo/ImageDrop";
import { NumberField, SelectField, TextField, Toggle } from "@/components/admin/catalogo/fields";
import { errorMessage } from "@/lib/errors";
import {
  GROUP_TYPE_LABEL,
  useCategories,
  useDeleteMockup,
  useDeletePiece,
  useFits,
  usePiece,
  useSaveMockup,
  useSetPieceGroups,
  useSizes,
  useThemes,
  useUpdatePiece,
  type AdminMockup,
} from "@/lib/catalog-admin";
import { formatBRL } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Editor de uma peça.
 *
 * Duas metades: os dados da peça (nome, preço base, arte) e a LISTA ORDENADA
 * de grupos que ela oferece. A ordem é a ordem em que o cliente responde as
 * perguntas no configurador, então ela é editável aqui e não em um campo
 * escondido.
 */
export const Route = createFileRoute("/admin/_painel/catalogo/pecas/$pieceId")({
  head: () => ({ meta: [{ title: "Peça — Painel MonteBox" }] }),
  component: PieceEditor,
});

function PieceEditor() {
  const { pieceId } = Route.useParams();
  const navigate = useNavigate();

  const piece = usePiece(pieceId);
  const categories = useCategories();
  const themeOptions = useThemes();
  const sizeOptions = useSizes();
  const update = useUpdatePiece();
  const remove = useDeletePiece();
  const setGroups = useSetPieceGroups();

  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [themes, setThemes] = useState<string[]>([]);
  const [sizes, setSizes] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const [basePrice, setBasePrice] = useState<number | null>(null);
  const [cost, setCost] = useState<number | null>(null);
  const [minQuantity, setMinQuantity] = useState<number | null>(null);
  const [thumbnail, setThumbnail] = useState("");
  const [baseImage, setBaseImage] = useState("");
  const [baseImageBack, setBaseImageBack] = useState("");
  const [layerOrder, setLayerOrder] = useState<number | null>(null);
  const [active, setActive] = useState(false);

  const data = piece.data;

  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setCategory(data.category);
    setThemes(data.themes);
    setSizes(data.sizes);
    setDescription(data.description);
    setBasePrice(data.basePrice);
    setCost(data.cost);
    setMinQuantity(data.minQuantity);
    setThumbnail(data.thumbnail);
    setBaseImage(data.baseImage);
    setBaseImageBack(data.baseImageBack ?? "");
    setLayerOrder(data.layerOrder);
    setActive(data.active);
  }, [data]);

  if (!data) {
    return (
      <AdminCard className="m-8">
        <ListState
          loading={piece.isLoading}
          error={piece.error}
          empty={!piece.isLoading}
          emptyLabel="Peça não encontrada."
        />
      </AdminCard>
    );
  }

  const attachedIds = data.groups.map((group) => group.id);

  const reorder = (index: number, delta: number) => {
    const ids = [...attachedIds];
    const target = index + delta;
    const a = ids[index];
    const b = ids[target];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[target] = a;
    setGroups.mutate({ id: data.id, groupIds: ids });
  };

  return (
    <>
      <header className="flex h-16 shrink-0 items-center justify-between gap-4 border-b border-mb-line bg-white px-8">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/catalogo"
            aria-label="Voltar para o catálogo"
            className="grid h-9 w-9 place-items-center rounded-full border border-mb-line text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
          >
            <ArrowLeft className="h-[18px] w-[18px]" />
          </Link>
          <Link to="/admin/catalogo" className="text-sm font-semibold text-mb-muted">
            Catálogo
          </Link>
          <ChevronRight className="h-4 w-4 text-mb-muted" />
          <h1 className="font-display text-xl font-bold tracking-tight text-mb-navy">
            {data.name}
          </h1>
        </div>

        <button
          type="button"
          disabled={remove.isPending}
          onClick={() => {
            if (!window.confirm(`Excluir a peça "${data.name}"?`)) return;
            remove.mutate(data.id, {
              onSuccess: async () => {
                toast.success("Peça excluída.");
                await navigate({ to: "/admin/catalogo" });
              },
              onError: (cause) => toast.error(errorMessage(cause)),
            });
          }}
          className="flex items-center gap-1.5 rounded-full border border-mb-line px-3.5 py-2 text-xs font-bold text-destructive transition-colors hover:border-destructive disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Excluir peça
        </button>
      </header>

      <div className="grid items-start gap-6 p-4 sm:p-8 xl:grid-cols-[1fr_460px]">
        {/* Dados da peça */}
        <AdminCard className="p-6">
          <h2 className="font-display text-base font-bold text-mb-navy">Dados da peça</h2>

          <div className="mt-5 grid grid-cols-2 gap-4">
            <TextField label="Nome" value={name} onChange={setName} />
            <SelectField
              label="Categoria"
              value={category}
              onChange={setCategory}
              options={(categories.data ?? []).map((item) => ({
                value: item.id,
                label: item.label,
              }))}
            />
            <TextField
              label="Descrição"
              value={description}
              onChange={setDescription}
              className="col-span-2"
            />
            <ThemePicker options={themeOptions.data ?? []} selected={themes} onChange={setThemes} />
            <div className="col-span-2 flex flex-col gap-1.5">
              <span className="text-xs font-bold text-mb-ink">Tamanhos</span>
              {(sizeOptions.data ?? []).length === 0 ? (
                <p className="text-xs text-mb-muted">
                  Nenhum tamanho cadastrado.{" "}
                  <Link
                    to="/admin/catalogo"
                    search={{ aba: "tamanhos" }}
                    className="font-semibold text-mb-navy underline"
                  >
                    Cadastrar tamanhos
                  </Link>
                </p>
              ) : (
                <ChipPicker options={sizeOptions.data ?? []} selected={sizes} onChange={setSizes} />
              )}
              <span className="text-[11px] leading-4 text-mb-muted">
                Tamanhos em que a peça é vendida. Com algum marcado, a loja passa a perguntar o
                tamanho; sem nenhum, não pergunta.
              </span>
            </div>
            <div className="col-span-2 grid grid-cols-3 gap-4">
              <NumberField label="Preço base" value={basePrice} onChange={setBasePrice} />
              <NumberField
                label="Custo da peça"
                value={cost}
                onChange={setCost}
                hint={
                  cost === null
                    ? "Interno. Sem custo, o lucro desta peça fica estimado."
                    : basePrice
                      ? `Margem ${(((basePrice - cost) / basePrice) * 100).toFixed(1).replace(".", ",")}% · lucro ${formatBRL(basePrice - cost)} por peça base.`
                      : "Interno — nunca aparece na loja."
                }
              />
              <NumberField
                label="Ordem no preview"
                value={layerOrder}
                onChange={setLayerOrder}
                step="1"
                hint="Maior desenha por cima das outras peças do kit."
              />
              <NumberField
                label="Quantidade mínima"
                value={minQuantity}
                onChange={setMinQuantity}
                step="1"
                hint="Somando as linhas da peça no pedido. Em branco = sem mínimo."
              />
            </div>
            <div className="col-span-2 grid grid-cols-3 gap-4">
              <ImageDrop
                label="Miniatura"
                value={thumbnail}
                onChange={setThumbnail}
                folder="pecas"
                hint="O card da peça na loja."
              />
              <ImageDrop
                label="Base — frente"
                value={baseImage}
                onChange={setBaseImage}
                folder="pecas"
                hint="Trocada pelo mockup da modelagem, quando houver."
              />
              <ImageDrop
                label="Base — costas"
                value={baseImageBack}
                onChange={setBaseImageBack}
                folder="pecas"
                hint="Sem imagem, a loja não mostra a vista de costas."
              />
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between gap-4 border-t border-mb-line pt-5">
            <Toggle label="Disponível na loja" checked={active} onChange={setActive} />
            <button
              type="button"
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  {
                    id: data.id,
                    name,
                    category,
                    themes,
                    sizes,
                    description,
                    basePrice: basePrice ?? 0,
                    cost,
                    minQuantity,
                    thumbnail,
                    baseImage,
                    baseImageBack,
                    layerOrder: layerOrder ?? 10,
                    active,
                  },
                  {
                    onSuccess: () => toast.success("Peça atualizada."),
                    onError: (cause) => toast.error(errorMessage(cause)),
                  },
                )
              }
              className="flex items-center gap-2 rounded-mb-button bg-mb-yellow px-5 py-2.5 font-display text-sm font-bold text-mb-ink shadow-cta disabled:opacity-50"
            >
              {update.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4 stroke-[3]" />
              )}
              Salvar peça
            </button>
          </div>
        </AdminCard>

        {/* Grupos da peça */}
        <div className="flex flex-col gap-6">
          <MockupsCard pieceId={data.id} mockups={data.mockups} />

          <AdminCard className="overflow-hidden">
            <div className="border-b border-mb-line px-6 py-4">
              <h2 className="font-display text-base font-bold text-mb-navy">Opções desta peça</h2>
              <p className="mt-0.5 text-xs text-mb-muted">
                Na ordem em que aparecem no configurador.
              </p>
            </div>

            {data.groups.length === 0 && (
              <p className="px-6 py-8 text-center text-sm text-mb-muted">
                Nenhum grupo ainda. Adicione abaixo.
              </p>
            )}

            {data.groups.map((group, index) => (
              <div
                key={group.id}
                className="flex items-center gap-3 border-b border-mb-line px-6 py-3 last:border-b-0"
              >
                <span className="w-5 shrink-0 text-center font-mono text-xs text-mb-muted">
                  {index + 1}
                </span>

                <div className="min-w-0 grow">
                  <p className="flex items-center gap-2 truncate text-sm font-bold text-mb-ink">
                    {group.label}
                    {group.source !== "MANUAL" && (
                      <Lock className="h-3 w-3 shrink-0 text-mb-muted" />
                    )}
                  </p>
                  <p className="truncate text-[11px] text-mb-muted">
                    {GROUP_TYPE_LABEL[group.type]}
                    {group.type === "SINGLE" && ` · ${group.choices} opções`}
                    {` · grava em ${group.key}`}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    aria-label="Subir"
                    disabled={index === 0 || setGroups.isPending}
                    onClick={() => reorder(index, -1)}
                    className="grid h-8 w-8 place-items-center rounded-full border border-mb-line text-mb-navy disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label="Descer"
                    disabled={index === data.groups.length - 1 || setGroups.isPending}
                    onClick={() => reorder(index, 1)}
                    className="grid h-8 w-8 place-items-center rounded-full border border-mb-line text-mb-navy disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    aria-label={`Remover ${group.label}`}
                    disabled={setGroups.isPending}
                    onClick={() =>
                      setGroups.mutate(
                        {
                          id: data.id,
                          groupIds: attachedIds.filter((value) => value !== group.id),
                        },
                        {
                          onSuccess: () => toast.success(`"${group.label}" removido da peça.`),
                          onError: (cause) => toast.error(errorMessage(cause)),
                        },
                      )
                    }
                    className="grid h-8 w-8 place-items-center rounded-full border border-mb-line text-destructive disabled:opacity-30"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </AdminCard>

          <AdminCard className="overflow-hidden">
            <div className="border-b border-mb-line px-6 py-4">
              <h2 className="font-display text-base font-bold text-mb-navy">Grupos disponíveis</h2>
              <p className="mt-0.5 text-xs text-mb-muted">
                Adicionar um grupo aqui não o copia: ele continua o mesmo em todas as peças que o
                usam.
              </p>
            </div>

            <div className="max-h-[40vh] overflow-y-auto">
              {data.availableGroups.length === 0 && (
                <p className="px-6 py-8 text-center text-sm text-mb-muted">
                  Todos os grupos já estão nesta peça.
                </p>
              )}

              {data.availableGroups.map((group) => (
                <div
                  key={group.id}
                  className="flex items-center gap-3 border-b border-mb-line px-6 py-3 last:border-b-0"
                >
                  <div className="min-w-0 grow">
                    <p className="truncate text-sm font-bold text-mb-ink">{group.label}</p>
                    <p className="truncate text-[11px] text-mb-muted">
                      {GROUP_TYPE_LABEL[group.type]}
                      {group.pieces !== undefined && ` · usado por ${group.pieces} peça(s)`}
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={setGroups.isPending}
                    onClick={() =>
                      setGroups.mutate(
                        { id: data.id, groupIds: [...attachedIds, group.id] },
                        {
                          onSuccess: () => toast.success(`"${group.label}" adicionado.`),
                          onError: (cause) => toast.error(errorMessage(cause)),
                        },
                      )
                    }
                    className={cn(
                      "flex shrink-0 items-center gap-1.5 rounded-full border border-mb-line px-3.5 py-[7px] text-xs font-bold text-mb-navy transition-colors",
                      "hover:border-mb-navy hover:bg-mb-surface disabled:opacity-40",
                    )}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Adicionar
                  </button>
                </div>
              ))}
            </div>
          </AdminCard>
        </div>
      </div>
    </>
  );
}

/**
 * Mockups da peça — a arte mestre de cada modelagem.
 *
 * Cadastrar aqui é o que ABRE a modelagem na loja: o grupo "Modelagem" é
 * derivado desta lista, então uma peça sem mockup infantil simplesmente não
 * oferece infantil, em vez de oferecer e mostrar arte errada.
 */
function MockupsCard({ pieceId, mockups }: { pieceId: string; mockups: AdminMockup[] }) {
  const fits = useFits();
  const save = useSaveMockup();
  const remove = useDeleteMockup();

  const [adding, setAdding] = useState(false);
  const [fit, setFit] = useState("");
  const [frontUrl, setFrontUrl] = useState("");
  const [backUrl, setBackUrl] = useState("");

  const cadastradas = new Set(mockups.map((mockup) => mockup.fit));
  const disponiveis = (fits.data ?? []).filter((item) => !cadastradas.has(item.id));
  const escolhida = fit || disponiveis[0]?.id || "";

  const reset = () => {
    setAdding(false);
    setFit("");
    setFrontUrl("");
    setBackUrl("");
  };

  /** Trocar a arte de uma modelagem já cadastrada grava na hora. */
  const replace = (mockup: AdminMockup, change: { frontUrl?: string; backUrl?: string | null }) =>
    save.mutate(
      {
        pieceId,
        fit: mockup.fit,
        frontUrl: change.frontUrl ?? mockup.frontUrl,
        backUrl: change.backUrl === undefined ? mockup.backUrl : change.backUrl,
      },
      {
        onSuccess: () => toast.success(`Arte de "${mockup.label}" atualizada.`),
        onError: (cause) => toast.error(errorMessage(cause)),
      },
    );

  return (
    <AdminCard className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-mb-line px-6 py-4">
        <div>
          <h2 className="font-display text-base font-bold text-mb-navy">Modelagens desta peça</h2>
          <p className="mt-0.5 text-xs text-mb-muted">
            A loja só oferece a modelagem que tiver arte aqui.
          </p>
        </div>
        <button
          type="button"
          disabled={disponiveis.length === 0}
          title={
            disponiveis.length === 0
              ? "Todas as modelagens cadastradas já têm arte nesta peça"
              : "Adicionar modelagem"
          }
          onClick={() => setAdding((value) => !value)}
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-mb-line px-3.5 py-2 text-xs font-bold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" />
          Adicionar
        </button>
      </div>

      {adding && (
        <div className="border-b border-mb-line bg-mb-surface/60 px-6 py-4">
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Modelagem"
              value={escolhida}
              onChange={setFit}
              options={disponiveis.map((item) => ({ value: item.id, label: item.label }))}
            />
            <div />
            <ImageDrop label="Frente" value={frontUrl} onChange={setFrontUrl} folder="mockups" />
            <ImageDrop
              label="Costas"
              value={backUrl}
              onChange={setBackUrl}
              folder="mockups"
              hint="Sem imagem, a peça não tem vista de costas nesta modelagem."
            />
          </div>

          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={reset}
              className="flex h-9 items-center gap-1.5 rounded-full px-4 text-sm font-semibold text-mb-muted hover:text-mb-navy"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
            <button
              type="button"
              disabled={save.isPending || !escolhida || !frontUrl.trim()}
              onClick={() =>
                save.mutate(
                  {
                    pieceId,
                    fit: escolhida,
                    frontUrl: frontUrl.trim(),
                    backUrl: backUrl.trim() || null,
                  },
                  {
                    onSuccess: () => {
                      toast.success("Modelagem adicionada.");
                      reset();
                    },
                    onError: (cause) => toast.error(errorMessage(cause)),
                  },
                )
              }
              className="flex h-9 items-center gap-2 rounded-mb-button bg-mb-navy px-5 text-sm font-bold text-white disabled:opacity-50"
            >
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar arte
            </button>
          </div>
        </div>
      )}

      {mockups.length === 0 && !adding && (
        <p className="px-6 py-8 text-center text-sm text-mb-muted">
          Sem arte cadastrada — a peça não oferece nenhuma modelagem.
        </p>
      )}

      {mockups.map((mockup) => (
        <div
          key={mockup.fit}
          className="flex items-center gap-3 border-b border-mb-line px-6 py-3 last:border-b-0"
        >
          <ImageDrop
            size="sm"
            label={`${mockup.label} — frente`}
            value={mockup.frontUrl}
            removable={false}
            savesImmediately
            folder="mockups"
            onChange={(url) => replace(mockup, { frontUrl: url })}
          />
          <ImageDrop
            size="sm"
            label={`${mockup.label} — costas`}
            value={mockup.backUrl ?? ""}
            savesImmediately
            folder="mockups"
            onChange={(url) => replace(mockup, { backUrl: url || null })}
          />
          <div className="min-w-0 grow">
            <p className="truncate text-sm font-bold text-mb-ink">{mockup.label}</p>
            <p className="text-[11px] text-mb-muted">
              {mockup.backUrl ? "Frente e costas" : "Só frente"} · arraste uma imagem para trocar
            </p>
          </div>

          <button
            type="button"
            aria-label={`Remover ${mockup.label}`}
            disabled={remove.isPending}
            onClick={() => {
              if (!window.confirm(`Remover a modelagem "${mockup.label}" desta peça?`)) return;
              remove.mutate(
                { pieceId, fit: mockup.fit },
                {
                  onSuccess: () => toast.success("Modelagem removida da peça."),
                  onError: (cause) => toast.error(errorMessage(cause)),
                },
              );
            }}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-mb-line text-destructive disabled:opacity-30"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
    </AdminCard>
  );
}

/**
 * Temas da peça — marca/desmarca, vários ao mesmo tempo.
 *
 * Os temas são cadastrados na aba "Temas" do catálogo; aqui só se escolhe
 * quais valem para esta peça. Grava junto com "Salvar peça".
 */
function ThemePicker({
  options,
  selected,
  onChange,
}: {
  options: { id: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id]);

  return (
    <div className="col-span-2 flex flex-col gap-1.5">
      <span className="text-xs font-bold text-mb-ink">Temas</span>
      {options.length === 0 ? (
        <p className="text-xs text-mb-muted">
          Nenhum tema cadastrado.{" "}
          <Link
            to="/admin/catalogo"
            search={{ aba: "temas" }}
            className="font-semibold text-mb-navy underline"
          >
            Cadastrar temas
          </Link>
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {options.map((theme) => {
            const on = selected.includes(theme.id);
            return (
              <button
                key={theme.id}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(theme.id)}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors",
                  on
                    ? "border-mb-navy bg-mb-navy text-white"
                    : "border-mb-line bg-white text-mb-muted hover:text-mb-navy",
                )}
              >
                {on && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                {theme.label}
              </button>
            );
          })}
        </div>
      )}
      <span className="text-[11px] leading-4 text-mb-muted">
        Filtros em que a peça aparece na loja.
      </span>
    </div>
  );
}
